// Ball position nudging logic - ensures visual alignment with strike/ball calls
// Adapted from led-board-webui but with sport-specific ball sizes

interface NudgeParams {
  plateX: number; // -1 to 1 range (relative to zone center)
  plateY: number; // 0 to 1 range (bottom to top)
  isStrike: boolean;
  isSoftball?: boolean; // default false (baseball)
}

interface NudgeResult {
  plateX: number;
  plateY: number;
}

// SVG viewBox dimensions for strike zone visualization
const VIEWBOX_WIDTH = 381;
const VIEWBOX_HEIGHT = 477;

// Ball radius in SVG units
// Baseball: 31.5, Softball: 41
const BALL_RADIUS_BASEBALL = 31.5;
const BALL_RADIUS_SOFTBALL = 41;

// Gap/overlap for nudging (in SVG units)
const GAP_PX = 3; // minimum gap between ball and zone boundary for ball calls
const OVERLAP_PX = 3; // minimum overlap for strike calls

/**
 * Adjusts ball position so the visual always matches the pitch call:
 * - Ball calls: circle has a clear gap from the zone (never touching)
 * - Strike calls: circle always overlaps the zone boundary
 *
 * Processes relative coordinates (-1 to 1 for X, 0 to 1 for Y)
 * by converting to SVG space, nudging, then converting back.
 */
export function nudgeBallPosition({
  plateX,
  plateY,
  isStrike,
  isSoftball = false,
}: NudgeParams): NudgeResult {
  // Select ball radius based on sport
  const ballRadius = isSoftball ? BALL_RADIUS_SOFTBALL : BALL_RADIUS_BASEBALL;

  // Calculate clearance/reach in SVG units
  const ballClearance = ballRadius + GAP_PX; // center must be this far out for gap
  const strikeReach = ballRadius - OVERLAP_PX; // center can be this far out and still overlap

  // Convert relative coordinates to SVG space
  // X: -1 to 1 maps to left edge to right edge of zone (centered)
  const W = VIEWBOX_WIDTH / 2; // half-width for centering
  let x = W + plateX * W;

  // Y: 0 to 1 maps to bottom to top of zone (inverted for SVG)
  let y = VIEWBOX_HEIGHT - plateY * VIEWBOX_HEIGHT;

  // Apply nudging in SVG space
  if (isStrike) {
    // For strikes: pull back if too far outside the zone
    if (x < -strikeReach) x = -strikeReach;
    if (x > VIEWBOX_WIDTH + strikeReach) x = VIEWBOX_WIDTH + strikeReach;
    if (y < -strikeReach) y = -strikeReach;
    if (y > VIEWBOX_HEIGHT + strikeReach) y = VIEWBOX_HEIGHT + strikeReach;
  } else {
    // For balls: push out if too close to the zone
    if (x > 0 && x < ballClearance) x = ballClearance;
    if (x > VIEWBOX_WIDTH - ballClearance && x < VIEWBOX_WIDTH)
      x = VIEWBOX_WIDTH + ballClearance;
    if (y > 0 && y < ballClearance) y = ballClearance;
    if (y > VIEWBOX_HEIGHT - ballClearance && y < VIEWBOX_HEIGHT)
      y = VIEWBOX_HEIGHT + ballClearance;

    // Check if ball is overlapping from outside the zone (center is outside but circle reaches in)
    if (x + ballRadius > 0 && x < 0) x = -ballClearance;
    if (x - ballRadius < VIEWBOX_WIDTH && x > VIEWBOX_WIDTH)
      x = VIEWBOX_WIDTH + ballClearance;
    if (y + ballRadius > 0 && y < 0) y = -ballClearance;
    if (y - ballRadius < VIEWBOX_HEIGHT && y > VIEWBOX_HEIGHT)
      y = VIEWBOX_HEIGHT + ballClearance;

    // Edge case: center is inside the zone despite a ball call
    if (x >= 0 && x <= VIEWBOX_WIDTH && y >= 0 && y <= VIEWBOX_HEIGHT) {
      const distToLeft = x;
      const distToRight = VIEWBOX_WIDTH - x;
      const distToBottom = y;
      const distToTop = VIEWBOX_HEIGHT - y;
      const nearest = Math.min(
        distToLeft,
        distToRight,
        distToBottom,
        distToTop,
      );

      if (nearest === distToLeft) x = -ballClearance;
      else if (nearest === distToRight) x = VIEWBOX_WIDTH + ballClearance;
      else if (nearest === distToBottom) y = -ballClearance;
      else y = VIEWBOX_HEIGHT + ballClearance;
    }
  }

  // Convert back to relative coordinates
  const nudgedPlateX = (x - W) / W;
  const nudgedPlateY = (VIEWBOX_HEIGHT - y) / VIEWBOX_HEIGHT;

  return {
    plateX: nudgedPlateX,
    plateY: nudgedPlateY,
  };
}
